import {
	Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
	ChangeDetectorRef, ChangeDetectionStrategy, SimpleChange
} from '@angular/core';
import { ComponentBase } from 'common';
import { DiscResultsService } from 'discovery/services/discresults.service';
import { PrtgSettingsSummaryDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgSettingsSummaryDto';

// Dialog for the map-level "Export to PRTG (Data Only)" context-menu action.
// Mirrors the WinForms ExportToPrtgForm_DataOnly but with only the MVP fields:
// PRTG agent selection. Hosted-PRTG agents are listed but disabled because
// the server-side handler rejects them (this export needs the UVexplorer
// service running on the PRTG host, which is impossible for hosted PRTG).
@Component({
	selector: 'export-to-prtg-data-only',
	templateUrl: './export-to-prtg-data-only.component.html',
	styleUrls: ['./export-to-prtg-data-only.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportToPrtgDataOnlyComponent extends ComponentBase implements OnChanges {

	@Input() public display: boolean;
	@Input() public netViewGuid: string;

	@Output() public displayChange = new EventEmitter<boolean>();
	@Output() public onCancel = new EventEmitter<void>();
	@Output() public onExport = new EventEmitter<{ prtgSettingsGuid: string }>();

	public dropdownOptions: Array<{ label: string, value: string, disabled: boolean }> = [];
	public selectedGuid: string = "";
	public loading: boolean = false;
	public errorMessage: string = "";

	constructor(
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {
		super();
		this.display = false;
		this.netViewGuid = "";
	}

	public ngOnChanges(changes: SimpleChanges): void {
		const displayChange: SimpleChange = changes["display"];
		// Reload the dropdown each time the dialog is shown so changes the
		// user made to PRTG settings since the last open are picked up.
		if (displayChange && displayChange.currentValue === true && this.netViewGuid) {
			this.loadPrtgSettings();
		}
	}

	private loadPrtgSettings(): void {
		this.loading = true;
		this.errorMessage = "";
		this.dropdownOptions = [];
		this.selectedGuid = "";
		this.changeDetector.markForCheck();

		this.discResultsService.listPrtgSettings(this.netViewGuid).subscribe(
			(summaries: PrtgSettingsSummaryDto[]) => {
				this.loading = false;
				if (!summaries || summaries.length === 0) {
					this.errorMessage = "No PRTG settings are configured for this network. Add one in Protocol Settings first.";
				} else {
					this.dropdownOptions = summaries.map(s => ({
						label: this.formatLabel(s),
						value: s.prtg_settings_guid,
						disabled: s.is_hosted_prtg,
					}));
					// Auto-select the first non-disabled entry, if any.
					const firstEnabled = this.dropdownOptions.find(o => !o.disabled);
					this.selectedGuid = firstEnabled ? firstEnabled.value : "";
				}
				this.changeDetector.markForCheck();
			},
			(error) => {
				this.loading = false;
				this.errorMessage = "Failed to load PRTG agents.";
				this.changeDetector.markForCheck();
			});
	}

	private formatLabel(s: PrtgSettingsSummaryDto): string {
		let base = s.name || s.server_url || "(unnamed PRTG agent)";
		if (s.is_hosted_prtg) base += " — hosted PRTG (not supported for Data Only export)";
		return base;
	}

	public get enableExport(): boolean {
		return !this.loading && !!this.selectedGuid;
	}

	public hide(): void {
		this.display = false;
		this.displayChange.emit(this.display);
		this.changeDetector.markForCheck();
	}

	public cancel(): void {
		this.hide();
		this.onCancel.emit();
	}

	public export(): void {
		if (!this.enableExport) return;
		const chosen = this.selectedGuid;
		this.hide();
		this.onExport.emit({ prtgSettingsGuid: chosen });
	}
}
